import { useStore } from '@/store'
import { onlyUnique } from '@acme/common'
import { invariant } from '@acme/core'
import { ExpandedState } from '@tanstack/react-table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IDataTableProps } from '../components/DataTable'
import normalizePageSize from '../components/DataTable/normalizePageSize'
import FilterModelPro, { GridFilterModelPro } from '../utils/filterModelPro'
import { citaColunaInexistente } from '../utils/filtroPersistido'
import {
    GridColDefPro,
    GridSelectionModel,
    TDensity,
    ValidRowModel,
} from '../utils/types'
import { getVisibleChildren } from '../utils/visibleChildren'
import useColunasPersistidas from './useColunasPersistidas'
import useDefaultFilterModel, { filtroVazio } from './useDefaultFilterModel'
import useDefaultSelectionModel from './useDefaultSelectionModel'

export const optionsColumn: Partial<GridColDefPro> = {
    disableColumnMenu: true,
    disableExport: true,
    filterable: false,
    sortable: false,
    hideable: false,
    headerName: '',
    width: 60,
    align: 'center',
}

const SERVER_FILTER_DEBOUNCE_MS = 300

/**
 * Default do `columnVisibilityModel`, como constante de módulo: um `= {}` no
 * parâmetro nasce de novo a cada render e derrubaria o memo de `cols` — e com
 * ele a identidade dos colDefs, que é o que evita o React remontar as células.
 */
const SEM_VISIBILIDADE: NonNullable<
    IDataTableProps<ValidRowModel>['columnVisibilityModel']
> = {}

function normalizeServerFilterItem(item: GridFilterModelPro['items'][number]) {
    return {
        columnField: item.columnField,
        operatorValue: item.operatorValue,
        linkOperator: item.linkOperator,
        value:
            item.value === undefined || item.value === null
                ? item.value
                : `${item.value}`.trim(),
    }
}

function normalizeQuickFilterValue(value?: string) {
    return value?.trim() ?? ''
}

type OptionalFilterModelProps = {
    controlledFilterModel?: ReturnType<typeof useDefaultFilterModel>[0]
    setFilterModel?: ReturnType<typeof useDefaultFilterModel>[1]
}

type OptionalSelectionModelProps = {
    selectionModel?: ReturnType<typeof useDefaultSelectionModel>[0]
    onSelectionModelChange?: ReturnType<typeof useDefaultSelectionModel>[1]
}

interface UseDataGridProProps<T extends ValidRowModel> {
    density?: IDataTableProps<T>['density']
    rows?: IDataTableProps<T>['rows']
    columns: IDataTableProps<T>['columns']
    tableId?: IDataTableProps<T>['tableId']
    initialFilterModel?: IDataTableProps<T>['filterModel']
    filtroPadrao?: IDataTableProps<T>['filtroPadrao']
    defaultPageSize: NonNullable<IDataTableProps<T>['defaultPageSize']>
    pageSize: IDataTableProps<T>['pageSize']
    isLoading?: IDataTableProps<T>['loading']
    onRowClick?: IDataTableProps<T>['onRowClick']
    onCellEditCommit?: IDataTableProps<T>['onCellEditCommit']
    checkboxSelection?: IDataTableProps<T>['checkboxSelection']
    columnVisibilityModel?: IDataTableProps<T>['columnVisibilityModel']
    getRowClassName?: IDataTableProps<T>['getRowClassName']
    listView?: IDataTableProps<T>['listView']
    listViewColumn?: IDataTableProps<T>['listViewColumn']
    sortModel?: IDataTableProps<T>['sortModel']
    onSortModelChange?: IDataTableProps<T>['onSortModelChange']
    enableRowExpanding?: IDataTableProps<T>['enableRowExpanding']
    enableRowGrouping?: IDataTableProps<T>['enableRowGrouping']
    groupingModel?: IDataTableProps<T>['groupingModel']
    onGroupingModelChange?: IDataTableProps<T>['onGroupingModelChange']
    getRowCanExpand?: IDataTableProps<T>['getRowCanExpand']
    getDetailPanelContent?: IDataTableProps<T>['getDetailPanelContent']
    columnGroupingModel?: IDataTableProps<T>['columnGroupingModel']
    pageSizeOptions?: IDataTableProps<T>['pageSizeOptions']
    getRowsToExport?: IDataTableProps<T>['getRowsToExport']
    totalRows?: IDataTableProps<T>['totalRows']
    onPaginationChange?: IDataTableProps<T>['onPaginationChange']
    defaultPageIndex?: IDataTableProps<T>['defaultPageIndex']
    keepChildOnFilter?: IDataTableProps<T>['keepChildOnFilter']
    heightControls?: {
        rowHeight?: IDataTableProps<T>['rowHeight']
        headerHeight?: IDataTableProps<T>['headerHeight']
        getRowHeight?: IDataTableProps<T>['getRowHeight']
    }
}

type TProps<T extends ValidRowModel> = UseDataGridProProps<T> &
    OptionalFilterModelProps &
    OptionalSelectionModelProps

const useDataGridPro = <T extends ValidRowModel>({
    density: initialDensity = 'standard',
    rows = [],
    columns,
    tableId,
    initialFilterModel,
    controlledFilterModel,
    setFilterModel,
    filtroPadrao,
    defaultPageSize,
    pageSize: fixedPageSize,
    isLoading = false,
    onSelectionModelChange,
    selectionModel,
    checkboxSelection = false,
    columnVisibilityModel = SEM_VISIBILIDADE,
    heightControls = {},
    enableRowExpanding = false,
    enableRowGrouping = false,
    groupingModel = [],
    onGroupingModelChange,
    getRowCanExpand,
    getDetailPanelContent,
    totalRows,
    onPaginationChange,
    defaultPageIndex = 0,
    keepChildOnFilter,
    ...rest
}: TProps<T>) => {
    invariant(
        !getDetailPanelContent || !(enableRowExpanding || enableRowGrouping),
        'getDetailPanelContent não combina com enableRowExpanding nem enableRowGrouping',
    )
    const isServerSide =
        totalRows !== undefined && onPaginationChange !== undefined
    const [_filterModel, _onFilterModelChange] = useDefaultFilterModel(
        controlledFilterModel ? undefined : tableId,
        initialFilterModel,
        !!controlledFilterModel,
    )
    const [_selectionModel, _setSelecionModel] = useDefaultSelectionModel()
    const filterModel = isServerSide
            ? _filterModel
            : (controlledFilterModel ?? _filterModel),
        onFilterModelChange = setFilterModel ?? _onFilterModelChange
    const filterModelRef = useRef(filterModel)

    // Uma vez só, quando as colunas existem: antes disso todo item pareceria órfão.
    // Lê o filtro da tela: no server-side o interno só recebe a cópia num efeito
    // posterior, e no mount ainda está vazio.
    const isFiltroConferido = useRef(false)
    const itensTela = (controlledFilterModel ?? filterModel).items
    useEffect(() => {
        if (isFiltroConferido.current || !columns.length) return
        isFiltroConferido.current = true
        if (citaColunaInexistente(itensTela, columns))
            onFilterModelChange({
                ...filtroVazio,
                ...new FilterModelPro({ items: filtroPadrao?.items ?? [] })
                    .filterModel,
            })
    }, [columns, itensTela, filtroPadrao, onFilterModelChange])
    const [isFiltroVisivel, setFiltroVisivel] = useState<boolean>(false)
    const [visibilidadeColunas, setVisibilidadeColunas] =
        useState<boolean>(false)
    const { colunas, setColunas, resetColunas } = useColunasPersistidas(
        tableId,
        columns,
    )
    const [density, setDensity] = useState<TDensity>(initialDensity)
    const [expanded, setExpanded] = useState<ExpandedState>({})

    const { pageSize = defaultPageSize, setPageSize } = useStore(
        state => state.dataTable.pagination,
    )

    const [pagination, setPaginationState] = useState<{
        pageIndex: number
        pageSize: number
    }>({
        pageIndex: defaultPageIndex,
        pageSize:
            normalizePageSize(fixedPageSize) ?? normalizePageSize(pageSize),
    })
    const serverFilterTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
        filterModelRef.current = filterModel
    }, [filterModel])

    useEffect(() => {
        if (!isServerSide || controlledFilterModel === undefined) return

        _onFilterModelChange(controlledFilterModel)
    }, [controlledFilterModel, isServerSide])

    useEffect(() => {
        setPaginationState(prev =>
            prev.pageIndex === defaultPageIndex
                ? prev
                : { ...prev, pageIndex: defaultPageIndex },
        )
    }, [defaultPageIndex])

    useEffect(() => {
        return () => {
            if (serverFilterTimeoutRef.current) {
                clearTimeout(serverFilterTimeoutRef.current)
            }
        }
    }, [])

    const setPagination = useCallback(
        (
            updater: React.SetStateAction<{
                pageIndex: number
                pageSize: number
            }>,
        ) => {
            setPaginationState(prev => {
                const next =
                    typeof updater === 'function' ? updater(prev) : updater
                if (isServerSide) onPaginationChange?.(next)
                return next
            })
        },
        [isServerSide, onPaginationChange],
    )

    const gridData = useMemo(() => {
        if (isServerSide) return rows
        const model = new FilterModelPro(filterModel, columns)
        const prune = (row: any): any =>
            'children' in row
                ? {
                      ...row,
                      children: getVisibleChildren(
                          row.children,
                          model.filter,
                          keepChildOnFilter,
                      ).map(prune),
                  }
                : row
        return rows.filter(model.filter).map(prune)
    }, [rows, filterModel, isServerSide, keepChildOnFilter])

    // Reseta a paginação (apenas client-side, nunca quando onPaginationChange está definido)
    useEffect(() => {
        if (onPaginationChange !== undefined) return
        if (
            pagination.pageIndex >
            Math.ceil(gridData.length / pagination.pageSize) - 1
        ) {
            setPagination(prev => ({ ...prev, pageIndex: 0 }))
        }
    }, [gridData?.length, pagination.pageIndex, onPaginationChange])

    const cols = useMemo(
        () =>
            colunas.map(column => {
                if (Object.keys(columnVisibilityModel).includes(column.field))
                    column.hide = !columnVisibilityModel[column.field]
                return column
            }),
        [colunas, columnVisibilityModel],
    )

    function setSelectionModel(
        valueOrUpdater: React.SetStateAction<GridSelectionModel>,
    ) {
        onSelectionModelChange?.(valueOrUpdater)
        _setSelecionModel(valueOrUpdater)
    }

    // if gridData does not contains ids from selectionModel, remove it from selectionModel
    // Em server-side, os IDs de outras páginas não estão no gridData — pular cleanup
    useEffect(() => {
        if (isServerSide) return
        if (selectionModel && selectionModel.length > 0) {
            const ids = gridData.map(row => row.id)
            const newSelectionModel = selectionModel.filter(id =>
                ids.includes(id),
            )
            if (newSelectionModel.length !== selectionModel.length) {
                setSelectionModel(newSelectionModel)
            }
        }
    }, [gridData])

    invariant(
        gridData.map(it => String(it.id)).filter(onlyUnique).length ===
            gridData.length,
        'Cada linha deve ter um ID único',
    )

    const onFilterModelChangePro = useCallback(
        (updater: React.SetStateAction<GridFilterModelPro>) => {
            // Always update UI state immediately for responsive UX
            onFilterModelChange(updater)

            if (isServerSide) {
                // Update ref for consistency
                const newFilter =
                    typeof updater === 'function'
                        ? updater(filterModelRef.current)
                        : updater

                const prevComplete = filterModelRef.current.items
                    .filter(FilterModelPro.isItemComplete)
                    .map(normalizeServerFilterItem)
                const newComplete = newFilter.items
                    .filter(FilterModelPro.isItemComplete)
                    .map(normalizeServerFilterItem)
                const prevQuick = normalizeQuickFilterValue(
                    filterModelRef.current.quickFilterValue,
                )
                const newQuick = normalizeQuickFilterValue(
                    newFilter.quickFilterValue,
                )
                const hasEffectiveChange =
                    prevQuick !== newQuick ||
                    JSON.stringify(prevComplete) !== JSON.stringify(newComplete)

                _onFilterModelChange(newFilter)
                filterModelRef.current = newFilter

                if (!hasEffectiveChange) return

                setPaginationState(prev => ({ ...prev, pageIndex: 0 }))

                if (!hasEffectiveChange) return

                serverFilterTimeoutRef.current = setTimeout(() => {
                    setPaginationState(prev => {
                        const next = { ...prev, pageIndex: 0 }
                        onPaginationChange?.(next)
                        return next
                    })
                    onFilterModelChange(newFilter)
                }, SERVER_FILTER_DEBOUNCE_MS)
            }
        },
        [onFilterModelChange, isServerSide, onPaginationChange],
    )

    return {
        rows: gridData,
        filterLength: filterModel.items.length,
        columns: cols,
        filterModelPro: filterModel,
        filtroPadrao,
        onFilterModelChangePro,
        isFiltroVisivel,
        setFiltroVisivel,
        density,
        setDensity,
        visibilidadeColunas,
        setVisibilidadeColunas,
        setColunas,
        resetColunas,
        setPageSize: (value: number) => {
            setPageSize(value)
            setPagination(prev => ({
                ...prev,
                pageSize: value,
                pageIndex: isServerSide ? 0 : prev.pageIndex,
            }))
        },
        setPagination,
        pagination,
        totalRows,
        isLoading,
        selectionModel: selectionModel ?? _selectionModel,
        setSelectionModel,
        checkboxSelection,
        heightControls,
        expanded,
        onExpandedChange: setExpanded,
        enableRowExpanding,
        enableRowGrouping,
        groupingModel,
        onGroupingModelChange,
        getRowCanExpand,
        getDetailPanelContent,
        ...rest,
    }
}

export default useDataGridPro
